import { Box, Tab, Tabs, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import { BulkCaseAssignment } from '../BulkCaseAssignment';
import { getExceptionQueue, getSmartRetry } from './api';
import { BulkUserReassignmentTab } from './BulkUserReassignmentTab';
import { ExceptionQueueTab } from './ExceptionQueueTab';
import { SmartRetryTab } from './SmartRetryTab';
import { TeamCapacityTab } from './TeamCapacityTab';

interface TabPanelProps {
  children: React.ReactNode;
  value: number;
  index: number;
}

function TabPanel({ children, value, index }: TabPanelProps) {
  return (
    <Box
      role="tabpanel"
      hidden={value !== index}
      id={`manager-tab-panel-${index}`}
      aria-labelledby={`manager-tab-${index}`}
    >
      {value === index && <Box pt={3}>{children}</Box>}
    </Box>
  );
}

export function ManagerTabPanel() {
  const [activeTab, setActiveTab] = useState(0);

  // Hoist these queries so badge counts are visible without entering each tab.
  // React Query deduplicates: the child tabs reuse the same cache entry.
  const { data: exceptionData } = useQuery({
    queryKey: ['manager-tab-exception-queue', 'badge'],
    queryFn: () => getExceptionQueue({ page: 0, pageSize: 1 }),
    refetchOnWindowFocus: false,
  });
  const { data: smartRetryData } = useQuery({
    queryKey: ['manager-tab-smart-retry', 'badge'],
    queryFn: () => getSmartRetry({ page: 0, pageSize: 1, countOnly: true }),
    refetchOnWindowFocus: false,
  });

  const exceptionCount = exceptionData?.totalCount ?? 0;
  const smartRetryCount = smartRetryData?.totalCount ?? 0;

  return (
    <Box>
      <Typography variant="h5" mb={2}>
        Manager Tab
      </Typography>

      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs
          value={activeTab}
          onChange={(_, v) => setActiveTab(v)}
          aria-label="Manager tab sections"
        >
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Exception Queue</span>
                {exceptionCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: 'error.main',
                      color: 'error.contrastText',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {exceptionCount > 99 ? '99+' : exceptionCount}
                  </Box>
                )}
              </Box>
            }
            id="manager-tab-0"
            aria-controls="manager-tab-panel-0"
          />
          <Tab
            label="Team Capacity"
            id="manager-tab-1"
            aria-controls="manager-tab-panel-1"
          />
          <Tab
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <span>Smart Retry</span>
                {smartRetryCount > 0 && (
                  <Box
                    component="span"
                    sx={{
                      minWidth: 18,
                      height: 18,
                      borderRadius: '50%',
                      backgroundColor: 'warning.main',
                      color: 'warning.contrastText',
                      fontSize: '0.7rem',
                      fontWeight: 600,
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    {smartRetryCount > 99 ? '99+' : smartRetryCount}
                  </Box>
                )}
              </Box>
            }
            id="manager-tab-2"
            aria-controls="manager-tab-panel-2"
          />
          <Tab
            label="Bulk Assignment"
            id="manager-tab-3"
            aria-controls="manager-tab-panel-3"
          />
        </Tabs>
      </Box>

      <TabPanel value={activeTab} index={0}>
        <ExceptionQueueTab />
      </TabPanel>
      <TabPanel value={activeTab} index={1}>
        <TeamCapacityTab />
      </TabPanel>
      <TabPanel value={activeTab} index={2}>
        <SmartRetryTab />
      </TabPanel>
      <TabPanel value={activeTab} index={3}>
        <BulkUserReassignmentTab />
        <BulkCaseAssignment />
      </TabPanel>
    </Box>
  );
}
